import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export interface SignaturePadHandle {
  toDataURL: () => string | null;
  clear: () => void;
}

interface Props {
  id?: string;
  onInk?: (hasInk: boolean) => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad({ id = 'sig-canvas', onInk }, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  const paintBackground = () => {
    const cvs = canvasRef.current;
    const ctx = cvs?.getContext('2d');
    if (!cvs || !ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cvs.width, cvs.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useImperativeHandle(ref, () => ({
    toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? null,
    clear: () => { paintBackground(); onInk?.(false); },
  }));

  useEffect(() => {
    const cvs = canvasRef.current;
    const ctx = cvs?.getContext('2d');
    if (!cvs || !ctx) return;
    paintBackground();

    const getPos = (e: MouseEvent | TouchEvent): [number, number] => {
      const rect = cvs.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0]!.clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0]!.clientY : e.clientY;
      return [(clientX - rect.left) * (cvs.width / rect.width), (clientY - rect.top) * (cvs.height / rect.height)];
    };
    const start = (e: MouseEvent | TouchEvent) => { drawing.current = true; const [x, y] = getPos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e: MouseEvent | TouchEvent) => { if (!drawing.current) return; e.preventDefault(); const [x, y] = getPos(e); ctx.lineTo(x, y); ctx.stroke(); onInk?.(true); };
    const end = () => { drawing.current = false; };

    cvs.addEventListener('mousedown', start);
    cvs.addEventListener('mousemove', move);
    cvs.addEventListener('mouseup', end);
    cvs.addEventListener('mouseleave', end);
    cvs.addEventListener('touchstart', start);
    cvs.addEventListener('touchmove', move);
    cvs.addEventListener('touchend', end);
    return () => {
      cvs.removeEventListener('mousedown', start);
      cvs.removeEventListener('mousemove', move);
      cvs.removeEventListener('mouseup', end);
      cvs.removeEventListener('mouseleave', end);
      cvs.removeEventListener('touchstart', start);
      cvs.removeEventListener('touchmove', move);
      cvs.removeEventListener('touchend', end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'relative', border: '2px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <canvas ref={canvasRef} id={id} width={600} height={160} style={{ display: 'block', width: '100%', touchAction: 'none', cursor: 'crosshair' }} />
      <button type="button" onClick={() => { paintBackground(); onInk?.(false); }} style={{ position: 'absolute', top: 6, right: 8, background: 'rgba(0,0,0,.15)', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, color: '#333', cursor: 'pointer' }}>Clear</button>
    </div>
  );
});
